import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CMS_LANG_CODES, CMS_LANGUAGES } from '../utils/cmsLanguages';
import { isAdminAppRoute } from '../utils/adminRoutes';
import { STORAGE_KEYS } from '../utils/storageKeys';

const LanguageContext = createContext(null);

function readStoredLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SITE_LANGUAGE);
    if (stored && CMS_LANG_CODES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return 'en';
}

export function LanguageProvider({ children }) {
  const { pathname } = useLocation();
  const [language, setLanguageState] = useState(readStoredLanguage);
  const adminLtr = isAdminAppRoute(pathname);

  const setLanguage = useCallback((code) => {
    const next = CMS_LANG_CODES.includes(code) ? code : 'en';
    setLanguageState(next);
    try {
      localStorage.setItem(STORAGE_KEYS.SITE_LANGUAGE, next);
    } catch {
      /* ignore */
    }
  }, []);

  const meta = useMemo(
    () => CMS_LANGUAGES.find((l) => l.code === language) ?? CMS_LANGUAGES[0],
    [language]
  );

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = adminLtr ? 'ltr' : meta.dir || 'ltr';
  }, [language, meta.dir, adminLtr]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      label: meta.label,
      dir: meta.dir,
      languages: CMS_LANGUAGES,
    }),
    [language, setLanguage, meta.label, meta.dir]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
